import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Percent, Users, Store, AlertTriangle } from "lucide-react";

const iconMap = {
	commissions: Percent,
	noControl: Users,
	noBrand: Store,
	dependency: AlertTriangle,
};

export default function PainPoints() {
	const { t } = useTranslation();
	const primary = "#43BD98";

	const items = t("store.painPoints.items", { returnObjects: true });
	const painKeys = ["commissions", "noControl", "noBrand", "dependency"];

	return (
		<section className="py-20 bg-white font-[Poppins]">
			<div className="max-w-5xl mx-auto px-6">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="text-center mb-12"
				>
					<p
						className="uppercase text-sm font-semibold tracking-wide mb-3"
						style={{ color: primary }}
					>
						{t("store.painPoints.badge")}
					</p>
					<h2
						className="font-extrabold text-gray-900 mb-4"
						style={{ fontSize: "clamp(1.8rem, 5vw, 2.4rem)" }}
					>
						{t("store.painPoints.title")}
					</h2>
					<p className="text-gray-600 text-lg max-w-xl mx-auto">
						{t("store.painPoints.subtitle")}
					</p>
				</motion.div>

				{/* Pain Points Grid */}
				<div className="grid sm:grid-cols-2 gap-6 mb-12">
					{painKeys.map((key, index) => {
						const IconComponent = iconMap[key] || Percent;
						const item = items[key];

						return (
							<motion.div
								key={key}
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: index * 0.1 }}
								viewport={{ once: true }}
								className="flex gap-4 p-5 rounded-2xl bg-red-50/50 border border-red-100"
							>
								<div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
									<IconComponent className="w-6 h-6 text-red-500" />
								</div>
								<div>
									<h3 className="font-semibold text-gray-900 mb-1">
										{item.title}
									</h3>
									<p className="text-gray-600 text-sm leading-relaxed">
										{item.text}
									</p>
								</div>
							</motion.div>
						);
					})}
				</div>

				{/* Highlight Solution */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					viewport={{ once: true }}
					className="text-center p-8 rounded-3xl"
					style={{
						backgroundColor: `${primary}10`,
						border: `1px solid ${primary}25`,
					}}
				>
					<h3 className="text-xl font-bold text-gray-900 mb-2">
						{t("store.painPoints.highlight.title")}
					</h3>
					<p className="text-gray-700 mb-3">
						{t("store.painPoints.highlight.text")}
					</p>
					<p className="text-sm font-medium" style={{ color: primary }}>
						{t("store.painPoints.highlight.note")}
					</p>
				</motion.div>
			</div>
		</section>
	);
}
